import React, { createContext, startTransition, useContext, useEffect, useLayoutEffect, useMemo, useState } from "react"
import {
  getFallSaleSnapshot,
  preloadFallSaleFlags,
  whenFallSaleFlagsReady,
} from "./fall-sale-bootstrap"
import { FF_RPT_PROMOTION_FALL_SALE, FF_RPT_PROMOTION_FALL_SALE_ENDS_AT } from "./keys"
import { parseConfigCatEndsAtMs } from "./parse-ends-at"

const MAX_TIMEOUT_MS = 2147483647

/** @typedef {{ ready: boolean, enabled: boolean, endsAtMs: number|null }} FallSalePromotion */

/** @type {FallSalePromotion} */
const FALL_SALE_UNAVAILABLE = { ready: false, enabled: false, endsAtMs: null }

const FallSaleContext = createContext(/** @type {FallSalePromotion|null} */ (null))

// Avoid SSR useLayoutEffect warnings from Gatsby.
const useClientLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect

/**
 * Resolve ConfigCat fall-sale flags once (same keys as RPT).
 * Bootstrap starts in gatsby-browser onClientEntry; this provider only consumes it.
 * Local expiry follows RPT_PROMOTION_FALL_SALE__ENDS_AT only (Unix seconds).
 * If that flag is unset/0, the sale stays on until RPT_PROMOTION_FALL_SALE turns off.
 *
 * @returns {FallSalePromotion}
 */
function useFallSalePromotionState() {
  const [values, setValues] = useState(null)
  const [ready, setReady] = useState(false)
  const [expired, setExpired] = useState(false)

  // Apply bootstrap snapshot when ready; startTransition keeps Suspense hydration happy.
  useClientLayoutEffect(() => {
    const snap = getFallSaleSnapshot()
    if (snap?.values) {
      startTransition(() => {
        setValues(snap.values)
        setReady(true)
      })
    }

    let active = true
    preloadFallSaleFlags()
    whenFallSaleFlagsReady().then((next) => {
      if (!active || !next?.values) return
      startTransition(() => {
        setValues(next.values)
        setReady(true)
      })
    })

    return () => {
      active = false
    }
  }, [])

  const endsAtMs = useMemo(() => {
    if (!values) return null
    const milliseconds = parseConfigCatEndsAtMs(values[FF_RPT_PROMOTION_FALL_SALE_ENDS_AT])
    return milliseconds === undefined ? null : milliseconds
  }, [values])

  const flagOn = Boolean(values?.[FF_RPT_PROMOTION_FALL_SALE])

  useEffect(() => {
    if (!flagOn || typeof endsAtMs !== "number") {
      setExpired(false)
      return undefined
    }

    let timer
    const schedule = () => {
      const remaining = endsAtMs - Date.now()
      if (remaining <= 0) {
        setExpired(true)
        return
      }
      setExpired(false)
      timer = window.setTimeout(schedule, Math.min(remaining, MAX_TIMEOUT_MS))
    }

    schedule()
    return () => {
      if (timer) window.clearTimeout(timer)
    }
  }, [flagOn, endsAtMs])

  const pastDeadline = typeof endsAtMs === "number" && endsAtMs <= Date.now()
  const enabled = ready && flagOn && !expired && !pastDeadline

  return {
    ready,
    enabled,
    endsAtMs: flagOn ? endsAtMs : null,
  }
}

/**
 * Provide a single fall-sale resolution for Layout + page children (banner, pricing, download, follow, RPT).
 */
export function FallSaleProvider({ children }) {
  const value = useFallSalePromotionState()
  return <FallSaleContext.Provider value={value}>{children}</FallSaleContext.Provider>
}

/**
 * Black Friday / fall sale gate for PWS marketing surfaces.
 * Must be used under FallSaleProvider (Layout) so ConfigCat is read once per page.
 *
 * @returns {FallSalePromotion}
 */
export function useFallSalePromotion() {
  return useContext(FallSaleContext) || FALL_SALE_UNAVAILABLE
}
